import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, functions } from '../../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import Modal from '../../components/ui/Modal';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconTool,
  IconAlertTriangle,
  IconCheck,
  IconUser,
  IconClock,
  IconTractor,
  IconMapPin,
  IconBriefcase,
  IconLock,
} from '@tabler/icons-react';

const MIN_NARRATIVE = 80;

export default function DefectDetail() {
  const { t, language } = useTranslation();
  const { defectId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile, role } = useAuth();

  const [defect, setDefect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolution, setResolution] = useState('');
  const [narrative, setNarrative] = useState('');
  const [resolving, setResolving] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !defectId) return;
      try {
        const snap = await getDoc(doc(db, 'organisations', orgId, 'defects', defectId));
        if (snap.exists()) setDefect({ id: snap.id, ...snap.data() });
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, defectId]);

  // blocksMachine is stamped by onDefectCreated; fall back to severity for any
  // record raised before the lifecycle trigger existed.
  const blocks = defect
    ? (defect.blocksMachine === true ||
       (defect.blocksMachine === undefined && defect.severity === 'critical'))
    : false;

  const narrativeShort = Math.max(0, MIN_NARRATIVE - narrative.trim().length);
  const canResolve =
    resolution.trim().length > 0 && (!blocks || narrative.trim().length >= MIN_NARRATIVE);

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      await updateDoc(doc(db, 'organisations', orgId, 'defects', defectId), {
        status: 'in-progress',
        acknowledgedBy: userProfile?.name,
        acknowledgedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setDefect(prev => ({ ...prev, status: 'in-progress', acknowledgedBy: userProfile?.name }));
      toast.success(t('defects.acknowledged_toast'));
    } catch (err) {
      console.error(err);
      toast.error(t('defects.acknowledge_failed'));
    }
    setAcknowledging(false);
  };

  // Resolution goes through the callable rather than a direct write: it enforces
  // the manager check and the release narrative, and recomputes the machine's
  // blocked state. Writing straight to Firestore would bypass all three.
  const handleResolve = async () => {
    if (!canResolve) return;
    setResolving(true);
    try {
      const resolveDefect = httpsCallable(functions, 'resolveDefect');
      const res = await resolveDefect({
        orgId,
        defectId,
        resolution: resolution.trim(),
        narrative: blocks ? narrative.trim() : '',
      });

      const snap = await getDoc(doc(db, 'organisations', orgId, 'defects', defectId));
      if (snap.exists()) setDefect({ id: snap.id, ...snap.data() });

      setShowResolveModal(false);
      setResolution('');
      setNarrative('');
      toast.success(
        res?.data?.machineReleased ? t('defects.resolved_released') : t('defects.resolved_toast')
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.message || t('defects.resolve_failed'));
    }
    setResolving(false);
  };

  const handleEscalateToJob = () => {
    navigate(`/jobs/new?defectId=${defectId}`);
  };

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString(language, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const severityColour = (s) => {
    if (s === 'critical') return 'var(--color-danger)';
    if (s === 'major') return 'var(--color-warning)';
    if (s === 'moderate') return 'var(--color-info)';
    return 'var(--color-text-tertiary)';
  };

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>{t('common.loading')}</div>
    </Layout>
  );

  if (!defect) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/defects')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> {t('defects.title')}
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>{t('defects.not_found')}</div>
      </div>
    </Layout>
  );

  const photos = defect.photoUrls || defect.photos || [];
  const isManager = role === 'org_admin' || role === 'manager';

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 680, margin: '0 auto' }}>

        <div onClick={() => navigate('/defects')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}>
          <IconArrowLeft size={15} stroke={1.5} /> {t('defects.title')}
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h1 style={{ fontSize: 20, fontWeight: 400 }}>{t('defects.report_title')}</h1>
              <StatusPill status={defect.severity || 'minor'} size="lg" />
              <StatusPill status={defect.status || 'open'} size="lg" />
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              {t('defects.reported_by')} {defect.reportedBy} · {formatDateTime(defect.reportedAt)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {defect.status === 'open' && isManager && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleAcknowledge}
                disabled={acknowledging}
                loading={acknowledging}
              >
                {t('defects.acknowledge')}
              </Button>
            )}
            {defect.status !== 'resolved' && isManager && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<IconBriefcase size={13} stroke={1.5} />}
                  onClick={handleEscalateToJob}
                >
                  {t('defects.raise_job')}
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  icon={<IconCheck size={13} stroke={1.5} />}
                  onClick={() => setShowResolveModal(true)}
                >
                  {t('defects.resolve')}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Blocking banner */}
        {blocks && defect.status !== 'resolved' && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-lg)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <IconAlertTriangle size={16} color="var(--color-danger)" stroke={1.5} />
            <div style={{ fontSize: 13, color: 'var(--color-danger)' }}>
              {t('defects.blocking_banner')}
            </div>
          </div>
        )}

        {/* Details */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('defects.details_title')}</div>
          <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.6, marginBottom: 14, padding: '12px 14px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
            {defect.description || t('defects.no_description_provided')}
          </div>
          {[
            { icon: IconUser,    label: t('defects.reported_by'),   value: defect.reportedBy || '—'          },
            { icon: IconClock,   label: t('defects.reported_at'),   value: formatDateTime(defect.reportedAt)  },
            { icon: IconTractor, label: t('machines.col_machine'),  value: defect.machineName || '—'         },
            { icon: IconMapPin,  label: t('defects.location'),      value: defect.location || '—'            },
            { icon: IconTool,    label: t('defects.col_severity'),  value: defect.severity || '—', capitalise: true, severity: true },
          ].map(row => {
            const Icon = row.icon;
            return (
              <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                <Icon size={14} color="var(--color-text-tertiary)" stroke={1.5} />
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', width: 100, flexShrink: 0 }}>{row.label}</span>
                <span style={{ fontSize: 13, color: row.severity ? severityColour(defect.severity) : 'var(--color-text-primary)', textTransform: row.capitalise ? 'capitalize' : 'none' }}>
                  {row.value}
                </span>
              </div>
            );
          })}
        </Card>

        {/* Photos */}
        {photos.length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('defects.photos')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {photos.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt={`${t('checks.alt_defect_photo')} ${i + 1}`}
                  style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-light)', cursor: 'pointer' }}
                  onClick={() => window.open(url, '_blank')}
                />
              ))}
            </div>
          </Card>
        )}

        {/* Acknowledgement */}
        {defect.acknowledgedBy && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 10 }}>{t('defects.acknowledgement')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconCheck size={14} color="#185FA5" stroke={1.5} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{t('defects.acknowledged_by')} {defect.acknowledgedBy}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{formatDateTime(defect.acknowledgedAt)}</div>
              </div>
            </div>
          </Card>
        )}

        {/* Resolution */}
        {defect.status === 'resolved' && (
          <Card style={{ marginBottom: 16, border: '0.5px solid var(--color-success)' }}>
            <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-success)', marginBottom: 10 }}>{t('defects.status_resolved')}</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.6, marginBottom: 10 }}>
              {defect.resolution}
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              {t('defects.resolved_by')} {defect.resolvedBy} · {formatDateTime(defect.resolvedAt)}
            </div>
          </Card>
        )}

        {/* Return to service — the audit record, shown in full and never truncated */}
        {defect.unblockNarrative && (
          <Card style={{ marginBottom: 16, borderLeft: '2px solid #FF6A00', borderRadius: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 10 }}>{t('defects.return_to_service')}</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 10 }}>
              {defect.unblockNarrative}
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', paddingTop: 10, borderTop: '0.5px solid var(--color-border-light)' }}>
              {defect.unblockBy}
              {defect.unblockByRole ? ` · ${defect.unblockByRole}` : ''}
              {' · '}{formatDateTime(defect.unblockAt)}
            </div>
            {defect.unblockRevisedAt && (
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 6, fontStyle: 'italic' }}>
                {t('defects.revised')} {formatDateTime(defect.unblockRevisedAt)}
                {defect.unblockRevisedBy ? ` ${t('defects.revised_by_suffix').replace('{name}', defect.unblockRevisedBy)}` : ''}
              </div>
            )}
          </Card>
        )}

        {/* Machine link */}
        {defect.machineId && (
          <Button
            variant="secondary"
            fullWidth
            icon={<IconTractor size={14} stroke={1.5} />}
            onClick={() => navigate(`/machines/${defect.machineId}`)}
          >
            {t('defects.view_machine_profile')}
          </Button>
        )}

      </div>

      {/* Resolve modal */}
      <Modal
        open={showResolveModal}
        onClose={() => setShowResolveModal(false)}
        title={blocks ? t('defects.resolve_and_release') : t('defects.resolve_defect')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowResolveModal(false)}>{t('common.cancel')}</Button>
            <Button
              variant="success"
              onClick={handleResolve}
              disabled={resolving || !canResolve}
              loading={resolving}
              icon={<IconCheck size={14} stroke={1.5} />}
            >
              {blocks ? t('defects.release_machine') : t('defects.mark_resolved')}
            </Button>
          </>
        }
      >
        {blocks && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-md)', padding: '10px 12px', display: 'flex', gap: 9, marginBottom: 14 }}>
            <IconLock size={15} color="var(--color-danger)" stroke={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 12, color: 'var(--color-danger)', lineHeight: 1.5 }}>
              {t('defects.release_warning')}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
            {t('defects.resolution_details')} <span style={{ color: 'var(--color-danger)' }}>*</span>
          </label>
          <textarea
            placeholder={t('defects.resolution_placeholder')}
            value={resolution}
            onChange={e => setResolution(e.target.value)}
            rows={3}
            style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            autoFocus
          />
        </div>

        {blocks && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              {t('machines.unblock_statement')} <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <textarea
              placeholder={t('defects.narrative_placeholder')}
              value={narrative}
              onChange={e => setNarrative(e.target.value)}
              rows={6}
              style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
            />
            <div style={{ fontSize: 11, color: narrativeShort > 0 ? 'var(--color-text-tertiary)' : 'var(--color-success)', marginTop: 5 }}>
              {narrativeShort > 0
                ? `${narrativeShort} ${t(narrativeShort === 1 ? 'defects.chars_needed_one' : 'defects.chars_needed_many')}`
                : `${narrative.trim().length} ${t('defects.characters')}`}
            </div>
          </div>
        )}

        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
          {t('defects.permanent_note')}
        </div>
      </Modal>

    </Layout>
  );
}
